/**
 * Teste de webhook SIMULADO, passo a passo, na ORDEM NORMATIVA do pipeline do
 * motor (src/ingest/pipeline.ts): assinatura sobre os bytes crus -> JSON ->
 * evento reconhecido -> barreira de idempotencia -> resposta.
 *
 * Decisoes nao obvias:
 *  - O resultado e DETERMINISTICO a partir do estado do segredo: com segredo,
 *    passa tudo e responde 202; sem segredo, para na assinatura com 401 e os
 *    passos seguintes ficam "nao chegou aqui" -- e exatamente o que o motor
 *    faz, e e a unica forma honesta de mostrar por que "colei a URL e nada
 *    acontece".
 *  - Os passos aparecem um a um com atraso curto para a pessoa LER a ordem,
 *    nao para fingir rede. O rodape diz que e simulado. Quando a rota
 *    `POST /api/settings/webhooks/:provider/test` existir, cada passo vem do
 *    servidor e este componente so troca a fonte.
 *  - Evento de teste nunca cria lead nem agenda ligacao: o texto do ultimo
 *    passo diz isso, e o registro que vai para o historico leva `test: true`.
 *  - Ao concluir, devolve UM `WebhookEventLog` para o cartao prender ao
 *    historico. O guard por `useRef` impede devolver duas vezes num re-render.
 *  - `onResultado` PRECISA ser estavel (useCallback no cartao): ele entra nas
 *    dependencias do efeito, e uma arrow inline reiniciaria a animacao a cada
 *    render do pai.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Footnote, Icon, Modal, type BadgeTone, type IconName } from '../../../components/ui';
import type { WebhookEventLog, WebhookIntegration } from '../types';
import { WEBHOOK_PROVIDER_INFO } from '../vocabulario';

type EstadoDoPasso = 'aguardando' | 'rodando' | 'ok' | 'falhou' | 'pulado';

interface Passo {
  readonly key: string;
  readonly titulo: string;
  readonly ok: string;
  readonly falha: string | null;
}

const INTERVALO_MS = 420;
const PASSO_DA_ASSINATURA = 1;

const ESTADO: Readonly<Record<EstadoDoPasso, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName }>> = {
  aguardando: { label: 'Aguardando', tone: 'neutral', icon: 'relogio' },
  rodando: { label: 'Verificando', tone: 'info', icon: 'atualizar' },
  ok: { label: 'OK', tone: 'good', icon: 'check' },
  falhou: { label: 'Falhou', tone: 'critical', icon: 'x' },
  pulado: { label: 'Não chegou aqui', tone: 'neutral', icon: 'saida' },
};

function montarPassos(integracao: WebhookIntegration, sequencia: number): readonly Passo[] {
  const info = WEBHOOK_PROVIDER_INFO[integracao.provider];
  return [
    {
      key: 'entrega',
      titulo: 'Entrega na URL',
      ok: `POST recebido em /webhooks/${integracao.provider}/… com o corpo em bytes crus, sem reserializar.`,
      falha: null,
    },
    {
      key: 'assinatura',
      titulo: info.assinaCorpo ? 'Assinatura sobre os bytes crus' : 'Token da plataforma',
      ok: `${info.esquema}, lido de ${info.onde}: conferido com o segredo configurado no motor.`,
      falha: `401 assinatura_invalida — o motor não tem segredo para ${info.label}. Sem ele, nenhum evento desta origem é confiável e nada entra na régua.`,
    },
    {
      key: 'json',
      titulo: 'Corpo JSON',
      ok: 'JSON válido. O parse acontece só DEPOIS da assinatura: nada é alterado antes de conferir a origem.',
      falha: null,
    },
    {
      key: 'evento',
      titulo: 'Evento reconhecido',
      ok: `${info.eventoDeTeste} → Carrinho abandonado, no vocabulário do motor. Valor em centavos inteiros e telefone em E.164.`,
      falha: null,
    },
    {
      key: 'idempotencia',
      titulo: 'Barreira de idempotência',
      ok: `Chave ${integracao.provider}:cart_abandoned:teste-${sequencia} inédita: gravada. Um reenvio igual responderia 200 sem fazer nada de novo.`,
      falha: null,
    },
    {
      key: 'resposta',
      titulo: 'Resposta à plataforma',
      ok: '202 · agendado para T+30min. Como é teste, não cria lead nem liga para ninguém: só prova que o caminho está aberto.',
      falha: null,
    },
  ];
}

/** Estado de cada passo dado quantos ja concluiram e onde (se) a cadeia falhou. */
function estadosDe(total: number, concluidos: number, rodando: boolean, falhaEm: number | null): readonly EstadoDoPasso[] {
  return Array.from({ length: total }, (_, i) => {
    if (i < concluidos) {
      return i === falhaEm ? 'falhou' : 'ok';
    }
    if (falhaEm !== null && concluidos > falhaEm) {
      return 'pulado';
    }
    if (i === concluidos && rodando) {
      return 'rodando';
    }
    return 'aguardando';
  });
}

function resultadoDoTeste(integracao: WebhookIntegration, sequencia: number, falhou: boolean): WebhookEventLog {
  const comum = {
    id: `SIMULADO-teste-${integracao.provider}-${sequencia}`,
    receivedAt: new Date().toISOString(),
    phoneE164: null,
    source: integracao.provider === 'generic' ? 'teste-do-painel' : null,
    test: true,
  };
  if (falhou) {
    return { ...comum, type: null, status: 'rejected', httpStatus: 401, reason: 'assinatura_invalida', productName: null, amountCents: null };
  }
  return { ...comum, type: 'cart_abandoned', status: 'accepted', httpStatus: 202, reason: null, productName: 'Evento de teste (sem lead)', amountCents: null };
}

export function TesteDeWebhook({
  open,
  onClose,
  integracao,
  sequencia,
  onResultado,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly integracao: WebhookIntegration;
  /** Numero do teste nesta sessao, para a chave de idempotencia mudar a cada clique. */
  readonly sequencia: number;
  readonly onResultado: (evento: WebhookEventLog) => void;
}): JSX.Element | null {
  const passos = montarPassos(integracao, sequencia);
  const total = passos.length;
  const falhaNaAssinatura = integracao.secretState === 'missing';
  const [estados, setEstados] = useState<readonly EstadoDoPasso[]>(() => estadosDe(total, 0, false, null));
  const [rodada, setRodada] = useState(0);
  const devolvido = useRef(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    devolvido.current = false;
    const falhaEm = falhaNaAssinatura ? PASSO_DA_ASSINATURA : null;
    const timers: number[] = [];
    let concluidos = 0;

    const avancar = (): void => {
      setEstados(estadosDe(total, concluidos, true, falhaEm));
      timers.push(
        window.setTimeout(() => {
          concluidos += 1;
          const acabou = concluidos >= total || (falhaEm !== null && concluidos > falhaEm);
          if (acabou) {
            setEstados(estadosDe(total, concluidos, false, falhaEm));
            if (!devolvido.current) {
              devolvido.current = true;
              onResultado(resultadoDoTeste(integracao, sequencia, falhaEm !== null));
            }
            return;
          }
          avancar();
        }, INTERVALO_MS),
      );
    };

    setEstados(estadosDe(total, 0, false, falhaEm));
    timers.push(window.setTimeout(avancar, 120));
    return () => {
      for (const t of timers) {
        window.clearTimeout(t);
      }
    };
  }, [open, rodada, falhaNaAssinatura, integracao, sequencia, total, onResultado]);

  const terminou = estados.every((e) => e !== 'aguardando' && e !== 'rodando');
  const sucesso = terminou && !falhaNaAssinatura;
  const info = WEBHOOK_PROVIDER_INFO[integracao.provider];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Testar webhook: ${info.label}`}
      description="Um evento de teste percorre o mesmo caminho de um evento real, na mesma ordem. Nada é enviado à plataforma."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button variant="secondary" icon="atualizar" disabled={!terminou} onClick={() => setRodada((r) => r + 1)}>
            Testar de novo
          </Button>
        </>
      }
    >
      <ol className="flex flex-col gap-2" aria-live="polite">
        {passos.map((passo, i) => {
          const estado = estados[i] ?? 'aguardando';
          const e = ESTADO[estado];
          const texto = estado === 'ok' ? passo.ok : estado === 'falhou' ? (passo.falha ?? passo.ok) : estado === 'pulado' ? 'O motor parou antes deste passo.' : null;
          return (
            <li
              key={passo.key}
              className={`flex gap-3 rounded-xl border px-3 py-2.5 ${
                estado === 'ok' ? 'border-status-good/40 bg-status-good/5' : estado === 'falhou' ? 'border-status-critical/40 bg-status-critical/5' : 'border-line bg-surface'
              }`}
            >
              <span
                aria-hidden="true"
                className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${
                  estado === 'ok' ? 'bg-status-good text-white' : estado === 'falhou' ? 'bg-status-critical text-white' : 'bg-ink/10 text-ink-subtle'
                }`}
              >
                {estado === 'ok' || estado === 'falhou' ? <Icon name={e.icon} size="xs" /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-ink">{passo.titulo}</p>
                  <Badge tone={e.tone} icon={estado === 'rodando' ? undefined : e.icon} dot={estado === 'rodando'}>
                    {e.label}
                  </Badge>
                </div>
                {texto !== null ? <p className={`mt-1 text-xs leading-relaxed ${estado === 'falhou' ? 'text-status-critical' : 'text-ink-muted'}`}>{texto}</p> : null}
              </div>
            </li>
          );
        })}
      </ol>

      {terminou ? (
        <div role="status" className={`mt-4 rounded-xl border px-4 py-3 text-sm ${sucesso ? 'border-status-good/40 bg-status-good/5' : 'border-status-critical/40 bg-status-critical/5'}`}>
          {sucesso ? (
            <>
              <p className="font-semibold text-ink">Caminho aberto: 202 em todos os passos.</p>
              <p className="mt-0.5 text-xs text-ink-muted">
                Um carrinho real que ficar 30 minutos sem pagar entra na régua por esta URL. O teste ficou registrado no histórico com o selo “Teste”.
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-ink">Parou na assinatura: 401.</p>
              <p className="mt-0.5 text-xs text-ink-muted">
                Peça à administração para configurar o segredo de {info.label} no motor. Até lá, todo evento desta origem é recusado e ninguém entra na régua.
              </p>
            </>
          )}
        </div>
      ) : null}

      <div className="mt-4">
        <Footnote>Teste simulado na prévia: os passos são os do motor, mas nenhuma requisição saiu deste navegador.</Footnote>
      </div>
    </Modal>
  );
}
